import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { getValidToken } from '../utils/tokenManager';

const API_BASE = API_BASE_URL;

/**
 * Custom hook for making API calls to Tegrat Rental API
 * Usage:
 *   const { data, loading, error, fetchVehicles, createVehicle } = useRentalApi();
 */
export function useRentalApi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = useCallback(async (endpoint, method = 'GET', body = null) => {
    try {
      setLoading(true);
      setError(null);

      // Get valid token (will auto-refresh if expired)
      const token = await getValidToken();

      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        }
      };

      // Add authorization header if token exists
      if (token) {
        options.headers['Authorization'] = `Bearer ${token}`;
      }

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${API_BASE}${endpoint}`, options);

      // If 401, redirect to login (token refresh failed)
      if (response.status === 401) {
        console.error('[useRentalApi] 401 Unauthorized - redirecting to login');
        localStorage.clear();
        window.location.href = '/dashboard';
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API call failed');
      }

      setData(result.data);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Vehicle operations
  const fetchVehicles = useCallback(() => {
    return apiCall('/vehicles', 'GET');
  }, [apiCall]);

  const fetchVehicleById = useCallback((id) => {
    return apiCall(`/vehicles/${id}`, 'GET');
  }, [apiCall]);

  const createVehicle = useCallback((vehicleData) => {
    return apiCall('/vehicles', 'POST', vehicleData);
  }, [apiCall]);

  const updateVehicle = useCallback((id, updates) => {
    return apiCall(`/vehicles/${id}`, 'PUT', updates);
  }, [apiCall]);

  const deleteVehicle = useCallback((id) => {
    return apiCall(`/vehicles/${id}`, 'DELETE');
  }, [apiCall]);

  // Store operations
  const fetchStores = useCallback(() => {
    return apiCall('/stores', 'GET');
  }, [apiCall]);

  // Vehicle operations with filters
  const fetchVehiclesWithFilters = useCallback((params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/vehicles?${queryString}` : '/vehicles';
    return apiCall(endpoint, 'GET');
  }, [apiCall]);

  // Transaction operations with filters
  const fetchTransactions = useCallback((params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/transactions?${queryString}` : '/transactions';
    return apiCall(endpoint, 'GET');
  }, [apiCall]);

  // Dashboard stats operations
  const fetchDashboardStats = useCallback(() => {
    return apiCall('/dashboard/stats', 'GET');
  }, [apiCall]);

  const fetchAdminStoresOverview = useCallback(() => {
    return apiCall('/dashboard/admin/stores-overview', 'GET');
  }, [apiCall]);

  const fetchRentalHeatmap = useCallback((params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/dashboard/rental-heatmap?${queryString}` : '/dashboard/rental-heatmap';
    return apiCall(endpoint, 'GET');
  }, [apiCall]);

  return {
    data,
    loading,
    error,
    fetchVehicles,
    fetchVehicleById,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    fetchStores,
    fetchVehiclesWithFilters,
    fetchTransactions,
    fetchDashboardStats,
    fetchAdminStoresOverview,
    fetchRentalHeatmap
  };
}

export default useRentalApi;
